import { AbstractDataProvider } from "./AbstractDataProvider";
import type { CreateParams, CreateResult, GetOneParams, GetOneResult, UpdateParams, UpdateResult } from "react-admin";
import z from "zod";
import {
  CreateDrivingLicenseAuthorityFormDto,
  schemas,
  serverAPI,
  UpdateDrivingLicenseAuthorityFormDto,
} from "../api/server.api";
import isEqual from "lodash/isEqual";

type DrivingLicenseAuthorityFormDto = z.infer<typeof schemas.DrivingLicenseAuthorityFormDto>;

export interface DrivingLicenseAuthorityForm extends DrivingLicenseAuthorityFormDto {
  pdf?: File;
}

class DrivingLicenseAuthorityFormsProvider extends AbstractDataProvider<DrivingLicenseAuthorityForm> {
  async getOne(
    _resource: string,
    { id }: GetOneParams<DrivingLicenseAuthorityForm>,
  ): Promise<GetOneResult<DrivingLicenseAuthorityForm>> {
    const i = id.indexOf(":");
    if (i <= 0) {
      throw new Error(`Invalid id: ${JSON.stringify(id)} -- expected a string of the format "<authorityId>:<formId>"`);
    }
    const [authorityId, formId] = id.split(":", 2);
    const form = await serverAPI.getDrivingLicenseAuthorityForm({ authorityId, id: formId });
    if (!form) {
      throw new Error(`DrivingLicenseAuthorityForm ${id} not found`);
    }
    return { data: { ...form, id } };
  }

  async create(_resource: string, params: CreateParams<DrivingLicenseAuthorityForm>): Promise<CreateResult> {
    const { authorityId, ...data } = params.data;
    if (!authorityId) {
      throw new Error("No authorityId in params.data");
    }
    if (typeof authorityId !== "string") {
      throw new Error("Invalid authorityId in params.data");
    }
    let createdForm = await serverAPI.createDrivingLicenseAuthorityForm({
      authorityId,
      dto: _toCreateDto({ ...data, drivingLicenseAuthorityId: authorityId }),
    });
    // Check if a PDF should be attached too
    if (data.pdf) {
      createdForm = await serverAPI.attachPdf({ authorityId, formId: createdForm.id, pdf: data.pdf });
    }
    return {
      data: { ...createdForm, id: `${authorityId}:${createdForm.id}` },
    };
  }

  async update(
    _resource: string,
    { id, data, previousData }: UpdateParams<DrivingLicenseAuthorityForm>,
  ): Promise<UpdateResult<DrivingLicenseAuthorityForm>> {
    const [authorityId, formId] = id.split(":");
    const dto = _toUpdateDto(data, previousData);
    let updatedForm = await serverAPI.updateDrivingLicenseAuthorityForm({ authorityId, formId, dto });
    // Check if a PDF should be attached too
    if (data.pdf) {
      updatedForm = await serverAPI.attachPdf({ authorityId, formId, pdf: data.pdf });
    }
    return {
      data: { ...updatedForm, id },
    };
  }
}

export const drivingLicenseAuthorityFormsProvider = new DrivingLicenseAuthorityFormsProvider();

function _toCreateDto(data: Partial<DrivingLicenseAuthorityForm>): CreateDrivingLicenseAuthorityFormDto {
  if (!data.drivingLicenseClasses) {
    data.drivingLicenseClasses = [];
  }
  return schemas.CreateDrivingLicenseAuthorityFormDto.parse(data);
}

function _toUpdateDto(
  data: Partial<DrivingLicenseAuthorityForm>,
  previousData: DrivingLicenseAuthorityForm,
): UpdateDrivingLicenseAuthorityFormDto {
  const delta = Object.fromEntries(Object.entries(data).filter(([k, v]) => !isEqual(v, previousData[k])));
  return schemas.UpdateDrivingLicenseAuthorityFormDto.parse(delta);
}
